/**
 * About page — shared js file for all about page section behaviours.
 *
 * Every custom About-page section loads this same file with
 *   <script type="module" src="{{ 'about-editorial.js' | asset_url }}"></script>
 * ES modules are deduplicated by URL, so it is fetched and executed exactly once no
 * matter how many chapter sections are on the page.
 *
 * Never append custom code to assets/theme.js — a Prestige update overwrites it.
 */

import { animate, inView, scroll } from 'vendor';
import { matchesMediaQuery } from 'theme';

/* Prestige's own reveal easing, lifted from ImageWithTextOverlay in theme.js. */
const REVEAL_EASING = [0.215, 0.61, 0.355, 1];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const readNumber = (element, attribute, fallback) => {
  const value = parseFloat(element.getAttribute(attribute));
  return Number.isFinite(value) ? value : fallback;
};

/**
 * Keyframes per reveal style. `mask` moves the big type only — it slides up out of a
 * clipping wrapper with no fade, which is what gives the editorial look. Supporting
 * copy always fades so it does not compete with the headline.
 */
const REVEAL_KEYFRAMES = {
  mask: (element) =>
    element.hasAttribute('data-hero-line')
      ? { transform: ['translateY(110%)', 'translateY(0)'] }
      : { opacity: [0, 1], transform: ['translateY(1.25rem)', 'translateY(0)'] },
  fade_up: () => ({ opacity: [0, 1], transform: ['translateY(1.25rem)', 'translateY(0)'] }),
  fade: () => ({ opacity: [0, 1] })
};

/**
 * <about-hero-video>
 *
 * Two behaviours, each opt-in through an attribute so the merchant can switch either
 * off in the theme editor:
 *
 *   [reveal]         line-by-line reveal of the hero text when the hero comes into view
 *   [scroll-effect]  the video pins to the screen and zooms out, darkens and blurs as the
 *                    hero scrolls away toward the next section
 *
 * Visibility contract — this is the part that matters. The hidden starting state is
 * written by the section's CSS behind `about-hero-video:defined`, so it only ever
 * applies once THIS file has loaded and registered the element. If the script fails
 * to load, `:defined` never matches and the hero renders as a plain, fully visible
 * banner. Content is never left invisible by a script that did not run.
 */
class AboutHeroVideo extends HTMLElement {
  #abortController;
  #stopEffect = null;
  #effectStarted = false;
  #skipTransition = false;

  constructor() {
    super();

    // The theme editor re-renders the whole section on every setting change. Without
    // this the reveal replays each time and the merchant cannot judge what they are
    // editing. `event.detail.load` is true when the section was just re-inserted, as
    // opposed to the merchant selecting it in the sidebar. Same guard Prestige uses.
    if (window.Shopify?.designMode) {
      this.closest('.shopify-section')?.addEventListener('shopify:section:select', (event) => {
        this.#skipTransition = event.detail.load;
      });
    }
  }

  connectedCallback() {
    this.#abortController = new AbortController();

    // Reduced motion: the section's CSS hides nothing in that case, so there is no
    // state to restore — leave the hero completely static.
    if (!matchesMediaQuery('motion-safe')) return;

    this.#setupReveal();

    // The effect can be limited to desktop, so re-check whenever that breakpoint is
    // crossed rather than only at connect time.
    this.#setupScrollEffect();

    if (this.hasAttribute('scroll-effect') && !this.hasAttribute('effect-mobile')) {
      window
        .matchMedia(window.themeVariables.mediaQueries['sm'])
        .addEventListener('change', () => this.#setupScrollEffect(), { signal: this.#abortController.signal });
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#teardownEffect();
  }

  #setupReveal() {
    if (!this.hasAttribute('reveal')) return;

    const items = Array.from(this.querySelectorAll('[data-hero-line], [data-hero-fade]'));
    const media = this.hasAttribute('reveal-media') ? Array.from(this.querySelectorAll('[data-hero-media]')) : [];

    if (items.length === 0 && media.length === 0) {
      this.setAttribute('revealed', '');
      return;
    }

    const duration = readNumber(this, 'reveal-duration', 900) / 1000;
    const step = readNumber(this, 'reveal-stagger', 120) / 1000;
    const keyframesFor = REVEAL_KEYFRAMES[this.getAttribute('reveal-style')] ?? REVEAL_KEYFRAMES.mask;

    inView(
      this,
      () => {
        // Hand the initial state over to the animations before any of them start.
        // Motion One runs every animation with fill: "both", so an item is held at
        // its first keyframe for the whole of its delay and at its last keyframe
        // afterwards. Dropping the CSS rule here can therefore never flash.
        this.setAttribute('revealed', '');

        // The video sits behind the text, so it fades in on its own ahead of the copy.
        // Only opacity is animated when the scroll effect is on: it owns `transform`/`filter`
        // on the media, and two animations of one property would fight.
        for (const element of media) {
          animate(
            element,
            this.hasAttribute('scroll-effect') ? { opacity: [0, 1] } : { opacity: [0, 1], scale: [1.08, 1] },
            { duration: duration * 1.4, easing: REVEAL_EASING }
          );
        }

        const animations = items.map((element, index) =>
          animate(element, keyframesFor(element), {
            duration,
            delay: index * step,
            easing: REVEAL_EASING
          })
        );

        if (this.#skipTransition) {
          animations.forEach((animation) => animation.finish());
          this.#skipTransition = false;
        }
      },
      { amount: 0.05 }
    );
  }

  // The scroll effect. The whole hero — video AND text — pins to the screen and recedes as one element:
  // it zooms out, blurs and darkens as the page scrolls on to the next section.
  //
  // <about-hero-video> stays in flow (it is the scroll spacer and the rectangle we measure). Inside it,
  // .about-hero__clip is pinned (position: fixed, via CSS behind [data-fixed]) and clipped every scroll
  // frame to the hero's on-screen rectangle, so nothing is ever painted over the next section. The clip
  // carries no transform, so its inset stays in true viewport coordinates; the zoom/blur/darken go on
  // .about-hero__stage inside it, so the two never desync.
  #setupScrollEffect() {
    if (!this.hasAttribute('scroll-effect')) return;

    // The effect can be limited to desktop. When not allowed, tear any running one down.
    const allowed = this.hasAttribute('effect-mobile') || matchesMediaQuery('sm');
    if (!allowed) {
      this.#teardownEffect();
      return;
    }

    if (this.#effectStarted) return;

    const clipper = this.querySelector('.about-hero__clip');
    const stage = this.querySelector('.about-hero__stage');
    if (!clipper || !stage) return;

    // `initialZoom` starts the video zoomed IN by this fraction; it zooms back out to exactly fill the
    // screen (scale 1) at the end, so the shrinking video never exposes a black edge. `darken` is a black
    // overlay: brightness(1 - darken) over the clip's black background is exactly an opacity-`darken` layer.
    const initialZoom = clamp(readNumber(this, 'effect-initial-zoom', 15), 0, 50) / 100;
    const darken = clamp(readNumber(this, 'effect-darken', 80), 0, 100) / 100;
    const maxBlur = clamp(readNumber(this, 'effect-blur', 12), 0, 40);

    // Once the clip is lifted out of flow the host would collapse; hold the section's scroll height at the
    // hero's height. The stage reports the fill (viewport-based) height whether or not the clip is fixed.
    const setSpacer = () => {
      this.style.minHeight = `${stage.getBoundingClientRect().height}px`;
    };

    const update = () => {
      const rect = this.getBoundingClientRect();
      const height = rect.height || 1;

      // 0 while the hero fills the screen (its top at the viewport top) → 1 once it has scrolled a full
      // hero-height past the top. The hero is the first section, so it starts at progress 0.
      const progress = clamp(-rect.top / height, 0, 1);

      // Measure against the layout viewport (clientWidth/Height), which EXCLUDES the scrollbar. Using
      // window.innerWidth (which includes it) made the right inset off by the scrollbar width and left a
      // thin gap down the right edge of the fixed video.
      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = document.documentElement.clientHeight;
      const top = Math.max(0, rect.top);
      const right = Math.max(0, viewportWidth - rect.right);
      const bottom = Math.max(0, viewportHeight - rect.bottom);
      const left = Math.max(0, rect.left);
      clipper.style.clipPath = `inset(${top}px ${right}px ${bottom}px ${left}px)`;

      // 1 + initialZoom at the top → 1 at the end: the video zooms out but never drops below full-screen,
      // so its edges can't reveal the black backdrop.
      stage.style.setProperty('--about-hero-zoom', (1 + initialZoom * (1 - progress)).toFixed(4));
      stage.style.setProperty('--about-hero-blur', `${(progress * maxBlur).toFixed(2)}px`);
      stage.style.setProperty('--about-hero-brightness', (1 - progress * darken).toFixed(4));
    };

    setSpacer();
    this.setAttribute('data-fixed', '');
    update();

    // scroll() with a bare callback fires on every scroll frame (rAF-throttled by Motion One); a resize
    // listener covers viewport changes (and the spacer height) that happen without scrolling.
    const onResize = () => {
      setSpacer();
      update();
    };
    this.#stopEffect = scroll(update);
    window.addEventListener('resize', onResize, { signal: this.#abortController.signal });

    this.#effectStarted = true;
  }

  #teardownEffect() {
    this.#stopEffect?.();
    this.#stopEffect = null;
    this.#effectStarted = false;
    this.removeAttribute('data-fixed');
    this.style.minHeight = '';

    const clipper = this.querySelector('.about-hero__clip');
    if (clipper) clipper.style.clipPath = '';

    const stage = this.querySelector('.about-hero__stage');
    if (stage) {
      stage.style.removeProperty('--about-hero-zoom');
      stage.style.removeProperty('--about-hero-blur');
      stage.style.removeProperty('--about-hero-brightness');
    }
  }
}

if (!window.customElements.get('about-hero-video')) {
  window.customElements.define('about-hero-video', AboutHeroVideo);
}
